import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import type { BrowseCategoryRawFacet } from '@/utils/browsePath/browsePath';

import { SearchResultsAdContainer } from '@/components/Ads/SearchResultsAd/SearchResultsAdContainer';
import { ArticleLinksSection } from '@/components/ArticleLinksSection/ArticleLinksSection';
import { CompleteAccountBanner } from '@/components/CompleteAccountBanner/CompleteAccountBanner';
import Pagination from '@/components/Pagination/hammer-pagination/Pagination';
import { PAGE_ITEMS_TO_VIEW } from '@/components/Pagination/helpers/filterSortOptions';
import { RelatedSearches } from '@/components/RelatedSearches/RelatedSearches';
import { SearchError } from '@/components/SearchError/SearchError';
import { SearchSignInInterstitial } from '@/components/SearchSignInInterstitial/SearchSignInInterstitial';
import { useOptimizedSearchSelectors } from '@/hooks/useOptimizedSearchSelectors';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { changePaginationPageSize, getSearchPageSize } from '@/redux/modules/pagination';
import { getSaveSearchParams } from '@/redux/modules/saveSearch';
import { getSEOSearchTermData } from '@/redux/modules/search/search.selectors';
import paramTypes from '@/types/search/enums/paramTypes';
import { FilterAction } from '@/types/SearchFacet';
import { getStateName } from '@/utils/address';
import { TrackPageBottomReachedTrigger } from '@/utils/amplitude/analytics/pageBottomReached/TrackPageBottomReachedTrigger';
import { usePopularityScoreReRanking } from '@/utils/amplitude/experiments/hooks/usePopularityScoreReRanking';
import { useSearchTileLiveLotPositionExperiment } from '@/utils/amplitude/experiments/hooks/useSearchTileLiveLotPosition.experiment';
import cleanSearchTerms from '@/utils/cleanSearchTerms';
import { getCollectionConfig } from '@/utils/collections/collections.config';
import { CollectionLayout } from '@/utils/collections/collections.types';
import { formatLocationString } from '@/utils/formatLocationFromSlug';
import { isEmpty } from '@/utils/isEmpty/isEmpty';
import { modifyParamsWithPage, modifyParamsWithPageSize, parseQueryParams } from '@/utils/queryParams';
import { buildSearchQuery } from '@/utils/search/searchQuery';
import { resolveSearchSeoPathSubjectToBrowseFacetIds } from '@/utils/search/searchSeoPath';
import { getIsSearchPage, getIsSEOSearchPage, getIsSEOSearchPageWithLocation } from '@/utils/urls';

import { CollectionBanner } from './components/CollectionBanner/CollectionBanner';
import { RelatedSearchesSuggestions } from './components/RelatedSearchesSuggestions/RelatedSearchesSuggestions';
import { SearchPageHeader } from './components/SearchPageHeader/SearchPageHeader';
import { SearchPageHeaderMetaWrapper } from './components/SearchPageHeaderMetaWrapper/SearchPageHeaderMetaWrapper';
import { SearchPageSeoTitleAndCopy } from './components/SearchPageSeoContent/SearchPageSeoTitleAndCopy';
import { SearchPageTabs } from './components/SearchPageTabs/SearchPageTabs';
import { SearchPageZeroResults } from './components/SearchPageZeroResults/SearchPageZeroResults';
import { SearchResultsBreadcrumbs } from './components/SearchResultsBreadcrumbs/SearchResultsBreadcrumbs';
import { SeeWhatsPopularSection } from './components/SeeWhatsPopularSection/SeeWhatsPopularSection';
import { useGetAppliedFilterChipValues } from './hooks/useGetAppliedFilterChipValues';
import { useGetSearchPageCanonicalUrl } from './hooks/useGetSearchPageCanonicalUrl';
import { useHandleRefetchOnExcludedHouse } from './hooks/useHandleRefetchOnExcludedHouse';
import { useNavigateSearchPageWithParams } from './hooks/useNavigateSearchPageWithParams';
import { useRedirectToValidPage } from './hooks/useRedirectToValidPage';

const SearchPage = () => {
    const { pathname, search } = useLocation();

    // Undefined for any /collections/<slug> not in the allowlist -- those never reach this
    // component, they render NotFound (see isUnknownCollectionSoft404).
    const collection = getCollectionConfig(pathname);
    const isCollectionsPage = Boolean(collection);
    const isOnSearchPage = getIsSearchPage(pathname);
    const isSEOSearchPage = getIsSEOSearchPage(pathname) || getIsSEOSearchPageWithLocation(pathname);
    const queryParams = parseQueryParams(search);

    const pageSize = useAppSelector(getSearchPageSize);
    const seoSearchTermData = useAppSelector(getSEOSearchTermData);
    const dispatch = useAppDispatch();

    // Use optimized selectors to batch Redux calls and reduce re-renders
    const {
        archivedSearch: isArchivedSearchPage,
        currentFacets,
        searchState,
    } = useOptimizedSearchSelectors(queryParams, pathname);
    const {
        deepPageRequiresAuth,
        error: searchFailed,
        isInitialLoading,
        isZeroResults,
        submitted: isLoadingSearchResults,
        totalFound,
        totalPages,
        totalSoldItems,
    } = searchState;

    useGetAppliedFilterChipValues();

    // Experiment: sear-1021-popularity-score-re-ranking-v2
    usePopularityScoreReRanking();
    // Experiment: sear-1971-search-tile-live-lot-position (mounted for exposure only; SearchCardInfo reads the variant directly)
    useSearchTileLiveLotPositionExperiment();

    const navigateSearchPageWithParams = useNavigateSearchPageWithParams();

    const browseFacetOptionIdsFromSeoPath = useMemo(
        () => resolveSearchSeoPathSubjectToBrowseFacetIds(pathname, currentFacets as BrowseCategoryRawFacet[]),
        [pathname, currentFacets]
    );

    const searchQuery = useMemo(() => {
        return buildSearchQuery({
            browseFacetOptionIds: browseFacetOptionIdsFromSeoPath,
            pathname,
            queryParams,
            searchPageSize: pageSize,
            similarItems: true,
        });
    }, [browseFacetOptionIdsFromSeoPath, pathname, queryParams, pageSize]);
    const { citySlug, distance, page, ranges, region, searchTerm } = searchQuery;

    const displaySearchTerm = useMemo(() => {
        return seoSearchTermData?.term || cleanSearchTerms(searchTerm);
    }, [seoSearchTermData?.term, searchTerm]);

    const displayLocation = useMemo(() => {
        const locationString = formatLocationString(citySlug, region, getStateName);
        return locationString ? ` ${locationString}` : '';
    }, [citySlug, region]);

    // if this a SEO page, we need to add the keyword to the save search params from the slug.
    const saveSearchParams = getSaveSearchParams(
        { ...queryParams, keyword: searchTerm },
        pathname,
        false,
        undefined,
        currentFacets
    );

    useHandleRefetchOnExcludedHouse({ searchQuery });

    // The LAAC-2371 design hides the related-search chips (Figma 143:81847 is hidden);
    // the legacy star-wars page keeps them.
    const shouldShowRelatedSearchesSuggestions =
        Boolean(searchTerm) && collection?.layout !== CollectionLayout.Collection;

    // Redirect to page 1 if requested page exceeds totalPages (e.g. /search/ping/?page=1000 but totalPages is 4)
    useRedirectToValidPage({
        isInitialLoading,
        isLoadingSearchResults,
        page,
        queryParams,
        totalPages,
    });

    const changePageAndNavigate = (selected: number) => {
        const page = selected + 1;
        const newParams = modifyParamsWithPage(queryParams, page);
        navigateSearchPageWithParams({
            checkForPageRemoval: true,
            filterAction: FilterAction.PageChanged,
            params: newParams,
        });
    };

    const changePageSizeAndNavigate = (pageSize: number) => {
        dispatch(changePaginationPageSize(pageSize));
        changePaginationPageSize(pageSize);
        const newParams = modifyParamsWithPageSize(queryParams, pageSize);
        navigateSearchPageWithParams({
            checkForPageRemoval: true,
            filterAction: FilterAction.PageSizeChanged,
            params: newParams,
        });
    };

    const onChange = ({ page, pageSize }: { page: number; pageSize: number }) => {
        if (!pageSize) {
            changePageAndNavigate(page - 1);
        } else {
            changePageSizeAndNavigate(pageSize);
        }
    };

    let canonicalURL = useGetSearchPageCanonicalUrl();

    const { hasNoFilterTermOrRangeResults, hasNoKeywordSearchResults } = useMemo(() => {
        const filterTermQuery = queryParams[paramTypes.FILTER_TERM] || null;
        const filterTerms: string[] = filterTermQuery ? filterTermQuery.split(',') : [];

        const isLoading = isLoadingSearchResults || isInitialLoading;
        const hasNoResults = totalFound === 0 && !isLoading;
        const hasNoFilterTermOrRangeResults =
            (Boolean(filterTerms.length) ||
                Boolean(distance?.zipCode) ||
                !isEmpty(ranges) ||
                Boolean(queryParams.country) ||
                Boolean(queryParams.state) ||
                totalSoldItems > 0) &&
            hasNoResults;

        const hasNoKeywordSearchResults = !hasNoFilterTermOrRangeResults && hasNoResults && totalSoldItems === 0;

        return { hasNoFilterTermOrRangeResults, hasNoKeywordSearchResults };
    }, [distance?.zipCode, isInitialLoading, isLoadingSearchResults, queryParams, ranges, totalFound, totalSoldItems]);

    return (
        <div>
            {collection ? (
                <>
                    <div className="m-auto max-w-[1644px] px-md xsMax:px-sm">
                        <SearchResultsBreadcrumbs breadcrumbs={collection.breadcrumbs} />
                    </div>
                    <CollectionBanner
                        collection={collection}
                        isArchivedSearchPage={isArchivedSearchPage}
                    />
                </>
            ) : null}

            <div
                className={cn('m-auto max-w-[1644px] px-md py-md xsMax:px-sm xsMax:py-sm', {
                    'pt-sm': isCollectionsPage,
                    'pt-xs': isSEOSearchPage,
                })}
            >
                <SearchPageHeaderMetaWrapper
                    canonicalURL={canonicalURL}
                    collection={collection}
                    displayLocation={displayLocation}
                    displaySearchTerm={displaySearchTerm}
                    isSEOSearchPage={isSEOSearchPage}
                    page={page}
                    searchTerm={searchTerm}
                    totalFound={totalFound}
                />

                {isSEOSearchPage && <SearchResultsBreadcrumbs className="pb-sm" />}
                {isOnSearchPage && <CompleteAccountBanner className="mt-xs mb-lg max-w-full xsMax:mt-xs xsMax:mb-md" />}
                {searchFailed ? (
                    <SearchError />
                ) : deepPageRequiresAuth ? (
                    <SearchSignInInterstitial />
                ) : isZeroResults ? (
                    <SearchPageZeroResults
                        canonicalURL={canonicalURL}
                        distance={distance}
                        hasNoFilterTermOrRangeResults={hasNoFilterTermOrRangeResults}
                        isArchivedSearch={isArchivedSearchPage}
                        isSEOSearchPage={isSEOSearchPage}
                        onChange={onChange}
                        page={page}
                        pageSize={pageSize}
                        saveSearchParams={saveSearchParams}
                        searchTerm={searchTerm}
                        totalFound={totalFound}
                        totalPages={totalPages}
                        totalSoldItems={totalSoldItems}
                    />
                ) : (
                    <>
                        <SearchPageHeader
                            collection={collection}
                            displayLocation={displayLocation}
                            displaySearchTerm={displaySearchTerm}
                            hasNoKeywordSearchResults={hasNoKeywordSearchResults}
                            isArchivedSearch={isArchivedSearchPage}
                            isLoadingSearchResults={isLoadingSearchResults || isInitialLoading}
                            isSEOSearchPage={isSEOSearchPage}
                            saveSearchParams={saveSearchParams}
                            searchTerm={searchTerm}
                            totalFound={totalFound}
                            totalSoldItems={totalSoldItems}
                        />

                        {hasNoKeywordSearchResults ? (
                            // NO RESULTS
                            <SeeWhatsPopularSection />
                        ) : (
                            <>
                                {shouldShowRelatedSearchesSuggestions && <RelatedSearchesSuggestions />}
                                <Pagination
                                    canonicalUrl={canonicalURL}
                                    onChange={onChange}
                                    pageSizeOptions={PAGE_ITEMS_TO_VIEW}
                                    paginationFilter={{ page, pageSize, totalRecords: totalFound }}
                                    totalPages={totalPages}
                                >
                                    <SearchPageTabs
                                        distance={distance}
                                        hasNoFilterTermOrRangeResults={hasNoFilterTermOrRangeResults}
                                        isArchivedSearch={isArchivedSearchPage}
                                        isSEOSearchPage={isSEOSearchPage}
                                        searchTerm={searchTerm}
                                        totalSoldItems={totalSoldItems}
                                    />
                                </Pagination>
                                {isSEOSearchPage && seoSearchTermData && (
                                    <>
                                        <SearchPageSeoTitleAndCopy seoSearchTermData={seoSearchTermData} />
                                        <RelatedSearches
                                            containerClasses="mx-auto mt-lg max-w-[1240px] p-sm smMin:p-lg"
                                            headingMessageId="searchPage.seoPage.relatedSearches"
                                            labelKey="label"
                                            relatedTerms={seoSearchTermData?.relatedTerms || []}
                                            urlKey="url"
                                        />
                                    </>
                                )}
                                <TrackPageBottomReachedTrigger />
                                <SearchResultsAdContainer
                                    page={page}
                                    searchTerm={searchTerm}
                                />
                                <ArticleLinksSection className="mt-xl smMax:mt-2xl" />
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default SearchPage;
